import type { ISettingValueMaster } from "@bbj-types/setting";
import { AutocompleteSettings, type IAutocompleteSettingsProps } from "@inputs/autocomplete/AutocompleteSettings";
import { TextField } from "@mui/material";
import { Controller, type FieldPath, type FieldValues, type UseControllerProps } from "react-hook-form";

export interface IAutocompleteSettingsFormProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value extends Pick<ISettingValueMaster, "label" | "value" | "group">,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
> extends Omit<
      IAutocompleteSettingsProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
      "defaultValue" | "renderInput"
    >,
    UseControllerProps<TFieldValues, TName> {
  label?: string;
}

export const AutocompleteSettingsForm = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value extends Pick<ISettingValueMaster, "label" | "value" | "group">,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  label,
  ...props
}: IAutocompleteSettingsFormProps<TFieldValues, TName, Value, Multiple, DisableClearable, FreeSolo, ChipComponent>) => {
  return (
    <Controller
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      render={({ field, fieldState }) => (
        <AutocompleteSettings
          {...props}
          value={field.value}
          onChange={(_, value) => field.onChange(value)}
          onBlur={field.onBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              name={field.name}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputRef={field.ref}
            />
          )}
        />
      )}
    />
  );
};
